/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import DataRightsSection from "@/components/DataRightsSection";
import { Banner } from "@/components/landing/Banner";
import { FeatureCard } from "@/components/landing/FeatureCard";
import { Footer } from "@/components/landing/Footer";
import { GuideVideo } from "@/components/landing/GuideVideo";
import { Header } from "@/components/landing/Header";
import { ResponsePreview } from "@/components/landing/ResponsePreview";
import { UploadZone } from "@/components/landing/UploadZone";
import { Studio } from "@/components/studio/Studio";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { processDataPackage } from "@/lib/process-package";
import type { PackageStats, ProcessedChannel, ProcessingError } from "@/types";
import {
  Archive,
  ExternalLink,
  PanelRightOpen,
  Trash2,
  WifiOff,
} from "lucide-react";
import { useState } from "react";

const FEATURES = [
  {
    icon: PanelRightOpen,
    title: "Browse Messages",
    description:
      "Search and filter your chat history with a clean, private interface.",
  },
  {
    icon: Archive,
    title: "Save Important Content",
    description:
      "Download messages and attachments before deletion. Keep what matters to you.",
  },
  {
    icon: Trash2,
    title: "Request Server Message Removal",
    description:
      "Generate files for requesting deletion of server and group DM messages (1:1 DMs require DiscorchDeleter).",
  },
] as const;

interface AppState {
  currentScreen: "landing" | "studio";
  channels: ProcessedChannel[];
  stats: PackageStats | null;
  loading: boolean;
  error: ProcessingError | null;
}

const initialState: AppState = {
  currentScreen: "landing",
  channels: [],
  stats: null,
  loading: false,
  error: null,
};

export function App() {
  const [state, setState] = useState<AppState>(initialState);

  const handleDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { channels, stats } = await processDataPackage(acceptedFiles[0]);
      setState((prev) => ({
        ...prev,
        channels,
        stats,
        currentScreen: "studio",
        loading: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prev) => ({
        ...prev,
        error: {
          type: "PARSE_ERROR",
          message:
            err instanceof Error
              ? err.message
              : "There was a problem processing your file. Make sure it's a Discord data package ZIP.",
          details: err,
        },
        loading: false,
      }));
    }
  };

  function handleSetCurrentScreen(screen: "landing" | "studio") {
    setState(
      screen === "landing"
        ? initialState
        : (prev) => ({ ...prev, currentScreen: screen }),
    );
  }

  if (state.currentScreen === "studio") {
    if (!state.stats) throw new Error("Stats not available for Studio view");

    return (
      <Studio
        channels={state.channels}
        stats={state.stats}
        setCurrentScreen={handleSetCurrentScreen}
      />
    );
  }

  return (
    <>
      <Banner />
      <div className="bg-background flex min-h-screen flex-col px-4 py-8 md:py-12">
        <Header />

        <main className="mx-auto w-full max-w-4xl space-y-12">
          <div className="rounded-lg border-2 border-yellow-500/20 bg-yellow-500/5 p-4">
            <div className="flex items-start gap-3">
              <WifiOff className="mt-1 h-5 w-5 shrink-0 text-yellow-500" />
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium text-yellow-500">
                    Privacy Tip:
                  </span>{" "}
                  Consider disconnecting from the internet before uploading your
                  data package.
                </p>
                <p className="text-muted-foreground">
                  Your Discord data contains sensitive information. While this
                  app works fully offline and our{" "}
                  <a
                    href="https://codeberg.org/discorch/discorch"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground underline underline-offset-4"
                  >
                    code is open source
                  </a>
                  , you shouldn't trust any website blindly. Disconnect from the
                  internet for extra peace of mind — the app will work exactly
                  the same.
                </p>
              </div>
            </div>
          </div>

          <UploadZone
            loading={state.loading}
            error={state.error?.message ?? null}
            onDrop={handleDrop}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>

          <DataRightsSection />

          <Separator />

          <div className="mx-auto max-w-xl space-y-12">
            <h2 className="text-center text-lg font-medium">
              Simple Steps to Get Started
            </h2>

            <div className="space-y-12">
              <section className="space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-medium">
                  <span className="text-muted-foreground">01.</span>
                  Get Your Data
                </h3>
                <p className="text-muted-foreground text-sm">
                  Request your data package from Discord. Select "Messages" for
                  faster processing.
                </p>
                <div className="space-y-4">
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a
                      href="https://support.discord.com/hc/en-us/articles/360004957991"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Discord's Guide (support.discord.com)
                    </a>
                  </Button>

                  <Alert>
                    <AlertDescription>
                      Discord's guide is a bit outdated. See the video below.
                    </AlertDescription>
                  </Alert>

                  <GuideVideo />
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-medium">
                  <span className="text-muted-foreground">02.</span>
                  Prepare for Deletion (If Needed)
                </h3>
                <div className="space-y-3">
                  <p className="text-muted-foreground text-sm">
                    Before requesting deletion, understand Discord's
                    limitations:
                  </p>

                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-red-500">•</span>
                        <span>
                          <strong>1:1 DMs:</strong> Discord refuses to delete
                          these. Use{" "}
                          <a
                            href="https://github.com/Darker-Ink/DiscorchDeleter"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            DiscorchDeleter
                          </a>{" "}
                          instead.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-red-500">•</span>
                        <span>
                          <strong>Active servers/groups:</strong> You must leave
                          servers and group DMs before Discord will delete
                          messages from them.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-medium">
                  <span className="text-muted-foreground">03.</span>
                  Review Your Data
                </h3>
                <p className="text-muted-foreground text-sm">
                  Upload your data package to browse messages and prepare
                  deletion requests. Take your time to review — you can save
                  anything important before requesting deletion.
                </p>
              </section>

              <ResponsePreview />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
